import type { EdgeFinish, GlassVariant } from "../types/product";
import type { ProcessingRequest } from "../types/config";
import { FACET_MIN_DIKTE_MODEL_MM, facetMogelijk, type GrindFamily } from "./grinding";

/** Algemene minimum-maat per zijde (mm) voor glas op maat. */
const DEFAULT_MIN_SIDE_MM = 100;
/** Soorten zónder minimum-zijde (op verzoek): float, spiegels, mat, figuur, gelaagd,
 *  Lacobel-Safe en Matelac-Safe mogen kleiner dan de standaard minimum-zijde. */
const NO_MIN_SIDE_SUBCATS = new Set([
  "Floatglas",
  "Spiegels op maat",
  "Mat glas",
  "Figuurglas",
  "Gelaagd glas",
  "Lacobel-Safe",
  "Matelac-Safe",
]);

/**
 * Vanaf 12 mm moeten de randen minimaal vlak mat geslepen worden (prijslijst pag. 5): een dikke
 * plaat met een scherpe snijkant heeft een veel hoger breukrisico bij transport en montage.
 * Alleen de scherpe kant breken ("afscherpen") is niet genoeg — dat is geen vlakke slijping.
 */
export const MIN_GESLEPEN_DIKTE_MM = 12;

/** Randafwerkingen die de rand NIET vlak slijpen (alleen de snijkant breken). */
const NIET_GESLEPEN_FINISH_IDS = new Set(["afscherpen"]);

/** Of deze dikte verplicht een geslepen rand heeft. */
export function requiresGroundEdge(thicknessMm: number): boolean {
  return Number.isFinite(thicknessMm) && thicknessMm >= MIN_GESLEPEN_DIKTE_MM;
}

/**
 * Soorten waarvan de rand ALTIJD al bewerkt is: geharde glassoorten worden vóór het harden
 * afgescherpt (anders springt de ruit in de oven), en dat zit in de prijs. De 12 mm-regel gaat
 * over een rauwe snijkant en hoort daar dus niet te vuren — anders is bv. gehard-gelaagd 88.2
 * ineens niet meer te bestellen zonder losse PP-rand, terwijl de rand al afgescherpt geleverd
 * wordt. Zelfde patroon als de client gebruikt om de "Afgescherpt · inbegrepen"-tegel te tonen.
 */
// Installatieglas (deurglas) is óók gehard glas — dat wordt vóór het harden standaard afgescherpt,
// dus de scherpe rand breken zit er altijd al in.
const ALWAYS_ARRISED_RE = /gehard|hardglas|\besg\b|installatieglas|lacobel-?t\b|matelac-?t\b/i;

export function isAlwaysArrised(variant: Pick<GlassVariant, "subcategory" | "finish" | "name">): boolean {
  return ALWAYS_ARRISED_RE.test(`${variant.subcategory ?? ""} ${variant.finish ?? ""} ${variant.name ?? ""}`);
}

/** Of deze randafwerking als "vlak geslepen" telt (PP, facet, verstek, C-slijp …). */
export function isGroundEdge(edgeFinish?: EdgeFinish): boolean {
  return edgeFinish != null && !NIET_GESLEPEN_FINISH_IDS.has(edgeFinish.id);
}

/**
 * Of de rand rondom GEPOLIJST (PP) is. Een kaal facet telt hier niet mee: dat slijpt alleen de
 * schuine band, niet de hele rand — vandaar dat "facet + PP" wél voldoet.
 */
export function isPolishedEdge(edgeFinish?: EdgeFinish): boolean {
  const id = edgeFinish?.id;
  return id === "polijst" || (id != null && id.endsWith("+pp"));
}

/**
 * "Panelen met afgeronde hoeken dienen altijd rondom poli geslepen te worden" — zo staat het in
 * de werkbladen PP slijpen (enkel én gelaagd), en in het facet-werkblad komt het erop neer dat er
 * bij een ronde hoek alleen "facet + PP" te kiezen is. De hoekprijs uit de tabel gaat er namelijk
 * van uit dat de hoek meteen PP meekrijgt; zonder PP klopt die prijs niet.
 *
 * Geldt niet voor gehard-gelaagd: daar zet Timmers hoekafwerking, modellen en sparingen helemaal
 * uit, en rekenen wij een ronde hoek als paneeltoeslag.
 */
export function requiresPolishedEdge(
  processings: ProcessingRequest[] | undefined,
  family: GrindFamily = "enkel",
): boolean {
  if (family === "gehard-gelaagd") return false;
  return (processings ?? []).some((p) => p.kind === "hoek" && (p.hoekType ?? "rond") === "rond");
}

/** Of er een SCHUINE hoek gevraagd is — daar staat geen facetprijs voor in het prijzenboek. */
export function hasSchuineHoek(processings: ProcessingRequest[] | undefined): boolean {
  return (processings ?? []).some(
    (p) => p.kind === "hoek" && (p.hoekType === "schuin-poli" || p.hoekType === "schuin-brut"),
  );
}

export interface ValidateInput {
  /** Net production dimensions (after speling resolution). */
  netWidthMm: number;
  netHeightMm: number;
  quantity: number;
  variant: GlassVariant;
  edgeFinish?: EdgeFinish;
  /** Gevraagde bewerkingen — nodig voor de hoek-regels uit de werkbladen. */
  processings?: ProcessingRequest[];
  /** Glasfamilie; bepaalt of de hoek-regels gelden ("enkel" als niets is meegegeven). */
  grindFamily?: GrindFamily;
  /** Gekozen vorm — een facet op een model vraagt minimaal 6 mm glas. */
  shapeId?: string;
}

/**
 * Een validatie-melding als NL-template (met `{token}`-plaatshouders) + de bijbehorende waarden.
 * Zo blijft de melding vertaalbaar op de client (translateFormat kijkt de template op en vult de
 * tokens ná de vertaling in), terwijl de server hem via `formatValidationIssue` als NL-string kan
 * gebruiken. Catalogus-/afwerkingsnamen (`name`) blijven bewust onvertaald (net als elders).
 */
export interface ValidationIssue {
  template: string;
  params?: Record<string, string | number>;
}

/** Vult de `{token}`s in de NL-template in (zónder vertaling) — voor server/logging/exceptions. */
export function formatValidationIssue(issue: ValidationIssue): string {
  let s = issue.template;
  if (issue.params) for (const [k, v] of Object.entries(issue.params)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/**
 * Returns a list of validation issues (NL-template + params); empty if valid. Covers dimension
 * bounds, quantity and edge-thickness compatibility. This is the seam where Fase 2 hangs the
 * NEN 3569 / NEN 2608 safety rules.
 */
export function validateGlassConfig(input: ValidateInput): ValidationIssue[] {
  const { netWidthMm, netHeightMm, quantity, variant, edgeFinish, processings, grindFamily, shapeId } = input;
  const issues: ValidationIssue[] = [];

  // Geen per-zijde minimum meer (enkelglas): een zijde hoeft alleen een positieve maat te zijn.
  // De "minimum oppervlakte" wordt in de engine afgedwongen via variant.minAreaM2 (Math.max).
  if (!Number.isFinite(netWidthMm) || netWidthMm <= 0) issues.push({ template: "Breedte moet groter dan 0 mm zijn." });
  if (!Number.isFinite(netHeightMm) || netHeightMm <= 0) issues.push({ template: "Hoogte moet groter dan 0 mm zijn." });

  // Plaatmaat: het glas wordt uit een plaat gesneden, dus oriëntatie maakt niet uit — je
  // kunt de plaat draaien. Het past zolang de LANGSTE zijde ≤ de lange plaatzijde én de
  // KORTSTE zijde ≤ de korte plaatzijde. (maxWidthMm/maxHeightMm = de twee plaatmaten.)
  if (Number.isFinite(netWidthMm) && Number.isFinite(netHeightMm) && netWidthMm > 0 && netHeightMm > 0) {
    const plateLong = Math.max(variant.maxWidthMm, variant.maxHeightMm);
    const plateShort = Math.min(variant.maxWidthMm, variant.maxHeightMm);
    const longSide = Math.max(netWidthMm, netHeightMm);
    const shortSide = Math.min(netWidthMm, netHeightMm);
    if (longSide > plateLong || shortSide > plateShort) {
      issues.push({
        template: "Maximale plaatmaat is {plateShort} × {plateLong} mm (de plaat mag gedraaid worden) voor {name} — huidige maat {w} × {h} mm.",
        params: { plateShort, plateLong, name: variant.name, w: netWidthMm, h: netHeightMm },
      });
    }

    // Minimum-maat per zijde. Standaard 100 mm (behalve float/spiegel); per soort overschrijfbaar
    // via variant.minSideMm (bv. geëmailleerd 200) + een aparte minimum-langszijde (minLongSideMm).
    const minSide = variant.minSideMm ?? (NO_MIN_SIDE_SUBCATS.has(variant.subcategory ?? "") ? 0 : DEFAULT_MIN_SIDE_MM);
    if (minSide > 0 && shortSide < minSide) {
      issues.push(
        variant.minLongSideMm
          ? {
              template: "Minimale maat voor {name} is {minSide} mm per zijde (en {minLong} mm voor de langste zijde) — huidige maat {w} × {h} mm.",
              params: { name: variant.name, minSide, minLong: variant.minLongSideMm, w: netWidthMm, h: netHeightMm },
            }
          : {
              template: "Minimale maat voor {name} is {minSide} mm per zijde — huidige maat {w} × {h} mm.",
              params: { name: variant.name, minSide, w: netWidthMm, h: netHeightMm },
            },
      );
    } else if (variant.minLongSideMm != null && longSide < variant.minLongSideMm) {
      issues.push({
        template: "Minimale maat voor {name} is {minSide} × {minLong} mm — huidige maat {w} × {h} mm.",
        params: { name: variant.name, minSide, minLong: variant.minLongSideMm, w: netWidthMm, h: netHeightMm },
      });
    }

    // Minimum-diagonaal (bv. Matelac 250 mm).
    if (variant.minDiagonalMm != null) {
      const diag = Math.hypot(netWidthMm, netHeightMm);
      if (diag < variant.minDiagonalMm - 1e-9) {
        issues.push({
          template: "Minimale diagonaal voor {name} is {minDiag} mm — huidige diagonaal {diag} mm ({w} × {h} mm).",
          params: { name: variant.name, minDiag: variant.minDiagonalMm, diag: Math.round(diag), w: netWidthMm, h: netHeightMm },
        });
      }
    }

    // Hardglas-verhouding: een gehard paneel mag niet langwerpiger dan 1:max zijn
    // (standaard 1:10), anders vervormt het in de hardingsoven.
    if (variant.maxAspectRatio != null && shortSide > 0 && longSide / shortSide > variant.maxAspectRatio + 1e-9) {
      const minShort = Math.ceil(longSide / variant.maxAspectRatio);
      issues.push({
        template: "Bij {name} mag de verhouding maximaal 1:{ratio} zijn (anders vervormt het in de hardingsoven). Bij een lange zijde van {longSide} mm moet de korte zijde minimaal {minShort} mm zijn — huidige maat {w} × {h} mm.",
        params: { name: variant.name, ratio: variant.maxAspectRatio, longSide, minShort, w: netWidthMm, h: netHeightMm },
      });
    }
  }

  if (!Number.isInteger(quantity) || quantity < 1) {
    issues.push({ template: "Aantal moet een heel getal van minimaal 1 zijn." });
  }

  // Vanaf 12 mm is een geslepen rand verplicht (breukrisico) — "geen randafwerking" en losse
  // afscherping vallen af. Zowel de client als de server draaien deze check.
  if (requiresGroundEdge(variant.thicknessMm) && !isAlwaysArrised(variant) && !isGroundEdge(edgeFinish)) {
    issues.push({
      template:
        "Vanaf {min} mm moet de rand minimaal vlak geslepen worden (breukrisico) — kies bij {name} een geslepen randafwerking.",
      params: { min: MIN_GESLEPEN_DIKTE_MM, name: variant.name },
    });
  }

  // Ronde hoek → de plaat moet rondom gepolijst zijn (werkbladen, stap 4).
  if (requiresPolishedEdge(processings, grindFamily) && !isPolishedEdge(edgeFinish)) {
    issues.push({
      template:
        "Een plaat met een afgeronde hoek wordt altijd rondom gepolijst — kies Platpoly gepolijst (PP) of Facet + PP als randafwerking.",
    });
  }

  // Facet op een MODEL (toog, cirkel, ovaal, gebogen zijde) vraagt minimaal 6 mm glas; dunner is
  // het niet te slijpen. Staat zo in het facet-werkblad. Alleen voor enkel glas: gelaagd heeft
  // geen facettabel in de werkbladen en houdt de catalogusprijs.
  if (
    (grindFamily ?? "enkel") === "enkel" &&
    edgeFinish?.facetMm != null &&
    !facetMogelijk(variant.thicknessMm, shapeId)
  ) {
    issues.push({
      template:
        "Een facet op een {shape} kan pas vanaf {min} mm glas — kies een dikkere plaat of een andere randafwerking.",
      params: { shape: shapeId ?? "model", min: FACET_MIN_DIKTE_MODEL_MM },
    });
  }

  // Facet op een schuine hoek: daar staat geen prijs voor in het prijzenboek.
  if (hasSchuineHoek(processings) && edgeFinish?.facetMm != null) {
    issues.push({
      template: "Een facet kan niet op een schuine hoek — kies een andere randafwerking of een ronde hoek.",
    });
  }

  if (edgeFinish) {
    const { minThicknessMm, maxThicknessMm, name } = edgeFinish;
    if (minThicknessMm != null && variant.thicknessMm < minThicknessMm) {
      issues.push({ template: "{name} vereist minimaal {min} mm glasdikte.", params: { name, min: minThicknessMm } });
    }
    if (maxThicknessMm != null && variant.thicknessMm > maxThicknessMm) {
      issues.push({ template: "{name} kan maximaal tot {max} mm glasdikte.", params: { name, max: maxThicknessMm } });
    }
  }

  return issues;
}
